import { Button } from "src/components/button/Button"
import RadioInput from "src/components/radio-input/RadioInput"
import { RadioLabel } from "../../../modules/investment-module/InvestmentModule"
import s from "./FinalStep.module.scss"
import cn from "classnames"
import { InvestmentsGraph } from "src/components/investments-graph/InvestmentsGraph"
import { calculateIncomeFromRate } from "src/lib/investmentHandler"
import React from "react"
import { numberWithThousandSeparator } from "src/lib/numbers"
import { investmentDescriptionHandler } from "src/lib/investmentDescriptionHandler"
import { useBus } from "react-bus"
import { useRouter } from "next/router"
import InvestmentInput, { InvestmentInputSize } from "src/components/investment-input/InvestmentInput"

// Add types for Array.prototype.at as it's a new feature and TS will not compile
declare global {         
    interface Array<T> {                     
        at(index: number): T
    }
}
interface FinalStepProps {
    radioLabels: RadioLabel[]
    sharedClasses: {stepContainer: string}
    radioCheckedValue: string
    setRadioCheckedValue: (value: string) => void
    startInputText: string
    setInputText: React.Dispatch<React.SetStateAction<string>>
    monthlyInputText: string
    setMonthlyInputText: React.Dispatch<React.SetStateAction<string>>
    finalStepTitle: string
    finalStepCalculationDescription: string
    finalStepButtonLabel: string
    finalStepModalButtonText: string
    finalStepStartMoneyAmountSummary: string
    finalStepMonthlyMoneyAmountSummary: string
    finalStepRadioInputSummary: string
    rate: number
    period: number
    maxYears: number
    currency: string
    zeroInterestRateLabel: string
    estimatedRefundLabel: string
    tooltipTitle: string
    riskInfo: string
}

export default function FinalStep({ radioLabels, sharedClasses, radioCheckedValue, setRadioCheckedValue, startInputText, setInputText, monthlyInputText, setMonthlyInputText, finalStepTitle, finalStepCalculationDescription, finalStepButtonLabel, finalStepModalButtonText, finalStepStartMoneyAmountSummary, finalStepMonthlyMoneyAmountSummary, finalStepRadioInputSummary, rate, period, maxYears, currency, zeroInterestRateLabel, estimatedRefundLabel, tooltipTitle, riskInfo } : FinalStepProps) {

    const bus = useBus()

    const earnings = calculateIncomeFromRate(rate, Number(startInputText.replace(/\s/g, "")), Number(monthlyInputText.replace(/\s/g, "")), period, maxYears, radioCheckedValue === radioLabels[0].radioLabel)
    const flatEarnings = calculateIncomeFromRate(0.0, Number(startInputText.replace(/\s/g, "")), Number(monthlyInputText.replace(/\s/g, "")), period, maxYears, true)
    const allEarnings = React.useMemo(() => {
        const allEarnings = []
        earnings.forEach((e, index) => {
            const currentEarning = {
                year: e.x,
                earning: Math.round(e.y),
                flatEarning: Math.round(flatEarnings[index].y),
            }
            allEarnings.push(currentEarning)
        })

        return allEarnings
    }, [earnings, flatEarnings])

    const router = useRouter()

    const redirecthandler = React.useCallback(() => {
        /** Temporary hardcoded - to check if client wants to set this url */
        router.push(`https://se.lunar.app/skapa-konto`)
    }, [router])

    return (
        <div className={sharedClasses.stepContainer}>
            <div className={s["investment-step__text-container"]}>
                <p className="label-200">{finalStepTitle}</p>
                <p className={s["investment-step__money"]}>{numberWithThousandSeparator(allEarnings.at(-1).earning)} {currency}</p>
                <p className={s["investment-step__description"]} dangerouslySetInnerHTML={{ __html: investmentDescriptionHandler([
                    allEarnings.at(-1).earning - allEarnings.at(-1).flatEarning,
                    Number(startInputText.replace(/\s/g, "")),
                    Number(monthlyInputText.replace(/\s/g, "")),
                    maxYears,
                    rate,
                ], finalStepCalculationDescription) }}></p>
            </div>
            <div className={s["investment-step__content"]}>
                <div className={s["investment-step__graph-container"]}>
                    <div className={s["investment-step__graph-wrapper"]}>
                        <InvestmentsGraph data={allEarnings} currency={currency} zeroInterestRateLabel={zeroInterestRateLabel} estimatedRefundLabel={estimatedRefundLabel} tooltipTitle={tooltipTitle} showLegend={true}/>
                    </div>
                    <p className={s["investment-step__risk-info"]}>{riskInfo}</p>
                    <button onClick={() => bus.emit("investmentCalculation")}  className={cn(s["investment-step__modal-btn"], "label-50")}>{finalStepModalButtonText}</button>
                </div>
                <div className={s["investment-step__summary-container"]}>
                    <div className={s["investment-step__summary"]}>
                        <p className="paragraph-100">{finalStepStartMoneyAmountSummary}</p>
                        <div className={s["investment-step__money-amount"]}>
                            <InvestmentInput
                                size={ InvestmentInputSize.SMALL }
                                currency={currency}
                                placeholder={startInputText}
                                inputText={startInputText}
                                setInputText={setInputText}
                            />
                        </div>
                    </div>
                    <div className={s["investment-step__summary"]}>
                        <p className="paragraph-100">{finalStepMonthlyMoneyAmountSummary}</p>
                        <div className={s["investment-step__money-amount"]}>
                            <InvestmentInput
                                size={ InvestmentInputSize.SMALL }
                                max={ 50000 }
                                currency={currency}
                                placeholder={monthlyInputText}
                                inputText={monthlyInputText}
                                setInputText={setMonthlyInputText}
                            />
                        </div>
                    </div>
                    <div className={s["investment-step__summary"]}>
                        <p className="paragraph-100">{finalStepRadioInputSummary}</p>
                        <ul className={s["investment-step__radio-list--final"]}>
                            {radioLabels.map((radioLabel) => {
                                return (
                                    <RadioInput
                                        radioLabel={radioLabel.radioLabel}
                                        key={radioLabel.id}
                                        radioCheckedValue={radioCheckedValue}
                                        setRadioCheckedValue={setRadioCheckedValue}
                                        final
                                    />
                                )
                            })}
                        </ul>
                    </div>
                </div>
            </div>
            <Button onClick={() => redirecthandler()} pill>{finalStepButtonLabel}</Button>
        </div>
    )
}
